import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ROLE_OPTIONS, ROLE_LABELS } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash, X } from "@phosphor-icons/react";

function UserForm({ onClose, onSaved }) {
  const [f, setF] = useState({ email: "", password: "", name: "", role: "monitor" });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/users", f);
      toast.success("Usuário criado");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1C19]/60 flex items-center justify-center p-4">
      <div className="bg-white border border-[#E2E0D8] rounded-sm w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-[#E2E0D8]">
          <h3 className="font-heading font-bold text-xl tracking-tight">Novo usuário</h3>
          <button onClick={onClose} className="p-1">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <Field label="Nome *">
            <input required data-testid="user-name" value={f.name}
              onChange={(e) => set("name", e.target.value)} className="input" />
          </Field>
          <Field label="Email *">
            <input required type="email" data-testid="user-email" value={f.email}
              onChange={(e) => set("email", e.target.value)} className="input" />
          </Field>
          <Field label="Senha *">
            <input required type="password" minLength={4} data-testid="user-password"
              value={f.password} onChange={(e) => set("password", e.target.value)}
              className="input" />
          </Field>
          <Field label="Papel">
            <select data-testid="user-role" value={f.role} onChange={(e) => set("role", e.target.value)} className="input">
              {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button data-testid="submit-user" type="submit" disabled={saving} className="btn-primary">
              {saving ? "Salvando…" : "Criar usuário"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#4E544B] block mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [show, setShow] = useState(false);

  async function load() {
    const { data } = await api.get("/auth/users");
    setUsers(data);
  }

  useEffect(() => { load(); }, []);

  async function remove(id) {
    if (!window.confirm("Remover usuário?")) return;
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success("Usuário removido");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Usuários</h1>
          <p className="text-sm text-[#4E544B] mt-1">
            {users.length} usuários com acesso à plataforma.
          </p>
        </div>
        <button data-testid="new-user-btn" onClick={() => setShow(true)} className="btn-primary inline-flex items-center gap-2">
          <Plus size={16} weight="bold" /> Novo usuário
        </button>
      </div>

      <div className="bg-white border border-[#E2E0D8] rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F5F2] border-b border-[#E2E0D8]">
              <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#4E544B]">
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Papel</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-[#E2E0D8]">
                  <td className="px-4 py-3 font-semibold">{u.name}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#E9F5EC] text-[#2A5A3B] border border-[#2A5A3B]/20">
                      {ROLE_LABELS[u.role]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`delete-user-${u.email}`}
                      onClick={() => remove(u.id)}
                      className="text-[#C05746] hover:bg-[#FDEDED] p-2 rounded-sm"
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {show && <UserForm onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}
